const STORAGE_KEY = 'hq-sso';
const SIGNED_OUT_KEY = 'hq-sso-signed-out';
const CLIENT_ID = 'doctor-consult-web';
const USED_CODES_KEY = 'hq-sso-used-codes';

type PendingHqSso = {
  verifier: string;
  state: string;
};

export type HqSsoPrompt = 'none' | 'login';

/** Only the latest startHqSso may write sessionStorage and navigate (Strict Mode double-effect safe). */
let authorizeStartGeneration = 0;
let authorizeNavigating = false;

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function ssoBaseUrl(): string {
  const configured = (process.env.REACT_APP_SSO_URL || 'https://sso.aspenhealth.sg').replace(/\/$/, '');
  const onLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return onLocalHost ? 'http://localhost:5088' : configured;
}

export function hqSsoRedirectUri(): string {
  return `${window.location.origin}/sso/callback`;
}

export function markHqSignedOut(): void {
  sessionStorage.setItem(SIGNED_OUT_KEY, '1');
}

export function consumeHqSignedOut(): boolean {
  if (sessionStorage.getItem(SIGNED_OUT_KEY) !== '1') return false;
  sessionStorage.removeItem(SIGNED_OUT_KEY);
  return true;
}

export function hqSsoPostLogoutRedirectUri(): string {
  return `${window.location.origin}/login`;
}

export function hqSsoLogoutUrl(): string {
  const url = new URL('/connect/logout', ssoBaseUrl());
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('post_logout_redirect_uri', hqSsoPostLogoutRedirectUri());
  return url.toString();
}

export async function startHqSso(options?: { prompt?: HqSsoPrompt }): Promise<void> {
  if (authorizeNavigating) return;
  const myGen = ++authorizeStartGeneration;
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64Url(new Uint8Array(digest));
  const state = base64Url(crypto.getRandomValues(new Uint8Array(16)));
  // Stale concurrent start (e.g. React Strict Mode remount) must not overwrite PKCE pending.
  if (myGen !== authorizeStartGeneration || authorizeNavigating) return;
  authorizeNavigating = true;
  const pending: PendingHqSso = { verifier, state };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));

  const url = new URL('/connect/authorize', ssoBaseUrl());
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', hqSsoRedirectUri());
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  if (options?.prompt === 'none' || options?.prompt === 'login') {
    url.searchParams.set('prompt', options.prompt);
  }
  if (myGen !== authorizeStartGeneration) return;
  window.location.assign(url.toString());
}

const exchanges = new Map<string, Promise<unknown>>();

function rememberUsedCode(code: string): void {
  try {
    const raw = sessionStorage.getItem(USED_CODES_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(code)) {
      list.push(code);
      sessionStorage.setItem(USED_CODES_KEY, JSON.stringify(list.slice(-20)));
    }
  } catch {
    /* ignore */
  }
}

function wasCodeUsed(code: string): boolean {
  try {
    const raw = sessionStorage.getItem(USED_CODES_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    return list.includes(code);
  } catch {
    return false;
  }
}

export class HqSsoClientError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export function beginHqSsoExchange<T>(
  code: string | null,
  state: string | null,
  start: (pending: PendingHqSso) => Promise<T>,
): Promise<T> {
  if (!code || !state) {
    return Promise.reject(new HqSsoClientError(
      'SSO_MISSING_CODE',
      'HQ sign-in did not return a valid code. Start again from the login page.',
    ));
  }

  const existing = exchanges.get(code);
  if (existing) return existing as Promise<T>;

  if (wasCodeUsed(code)) {
    return Promise.reject(new HqSsoClientError(
      'SSO_CODE_ALREADY_USED',
      'This sign-in link was already used. Start again from the login page.',
    ));
  }

  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return Promise.reject(new HqSsoClientError(
      'SSO_CODE_ALREADY_USED',
      'This sign-in link was already used. Start again from the login page.',
    ));
  }

  const pending = JSON.parse(raw) as PendingHqSso;
  if (pending.state !== state || !pending.verifier) {
    return Promise.reject(new HqSsoClientError(
      'SSO_STATE_MISMATCH',
      'HQ sign-in state did not match. Start again from the login page.',
    ));
  }

  const job = start(pending).then(
    (result) => {
      sessionStorage.removeItem(STORAGE_KEY);
      rememberUsedCode(code);
      return result;
    },
    (err) => {
      sessionStorage.removeItem(STORAGE_KEY);
      rememberUsedCode(code);
      throw err;
    },
  );
  exchanges.set(code, job);
  return job;
}

export const buildLogoutUrl = hqSsoLogoutUrl;